// =============================================================================
// Mapbox map-tile cache — the numbers and the cache key that the service
// worker (vite.config.ts → runtimeCaching) and the "Save offline" pre-fetch
// (src/lib/offlineMapTiles.ts) must agree on.
//
// ⚠️ SIDE-EFFECT FREE ON PURPOSE: vite.config.ts imports this file while it
// builds the service worker, in Node. Nothing here may read `import.meta.env`,
// `window`, or import app code — offlineMapTiles.ts does both, which is why the
// numbers live here and not there.
//
// Why a cache of its own (until 5 Oct 2026 tiles shared `mapbox-assets-cache`
// with the style JSON, glyphs and sprites, capped at 1,500 entries):
//   * Workbox's ExpirationPlugin evicts least-recently-used entries past
//     `maxEntries` after EVERY write, across the whole cache. A save may ask for
//     up to MAX_TILE_URLS (12,000) URLs, so a large save evicted its own first
//     tiles — and the style the map needs to start at all — while it ran.
//   * The live map's tile URLs carry `sku=<random per map load>` (mapbox-gl
//     RequestManager.normalizeTileURL), and the pre-fetch's do not. Workbox
//     matches on the full URL, so the map never read a single pre-fetched tile.
//     `mapboxTileCacheKeyPlugin` below drops the query from the key, the same
//     rule mapbox-gl applies to its own tile cache (stripQueryParameters, which
//     keeps only language / worldview / jobid).
// =============================================================================

/** The most tile URLs one "Save offline" may request (≈2 per map tile: the
 *  satellite raster + the streets vector). Above this the save is refused and
 *  the operator is asked to zoom in. */
export const MAX_TILE_URLS = 12_000

/** Room for the tiles the live map caches while someone browses, so one full
 *  save plus a session of panning around fits without evicting the save. */
export const TILE_CACHE_HEADROOM = 3_000

/** The tile cache's entry cap: one full save + live browsing. */
export const MAPBOX_TILE_CACHE_MAX_ENTRIES = MAX_TILE_URLS + TILE_CACHE_HEADROOM

/** The runtime cache that holds `api.mapbox.com/v4` map tiles. The page opens
 *  it by this name to count what a save actually kept. */
export const MAPBOX_TILE_CACHE_NAME = 'mapbox-v4-tiles-cache'

/** The style JSON, glyphs, sprites and TileJSON (vite.config.ts writes the name
 *  out on its route; mapTileCache.test.ts pins the two together). */
export const MAPBOX_ASSETS_CACHE_NAME = 'mapbox-assets-cache'

/** Tiles served from the `*.tiles.mapbox.com` CDN hostname. */
const MAPBOX_CDN_TILES_CACHE_NAME = 'mapbox-tiles-cache'

/** EVERY cache that holds Mapbox content — the three routes that carry
 *  `mapboxCacheAgePlugin`. The page-side sweep (mapboxCacheSweep.ts) walks
 *  exactly these; a fourth Mapbox route must be added here or its unread
 *  entries outlive 30 days in storage. */
export const MAPBOX_CACHE_NAMES: readonly string[] = [
  MAPBOX_TILE_CACHE_NAME, MAPBOX_ASSETS_CACHE_NAME, MAPBOX_CDN_TILES_CACHE_NAME,
]

/** ⚠️ 30 days. Do NOT raise it. Mapbox Product Terms (21 July 2026) cl 2.8.1
 *  limits caching on the device to 30 days. ExpirationPlugin's `maxAgeSeconds`
 *  alone counts from LAST USE, so `mapboxCacheAgePlugin` below is what makes it
 *  30 days from DOWNLOAD. */
export const MAPBOX_CACHE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30

/** Response header the service worker stamps on every Mapbox entry it stores:
 *  the download time, epoch milliseconds. (mapboxCacheAgePlugin inlines it.) */
export const MAPBOX_DOWNLOADED_AT_HEADER = 'x-sw-downloaded-at'

/** A Mapbox v4 map TILE: `/v4/<tileset[,tileset…]>/<z>/<x>/<y>[@2x].<ext>`.
 *  TileJSON (`/v4/mapbox.satellite.json`) has no z/x/y and does not match, so it
 *  stays with the style, glyphs and sprites in `mapbox-assets-cache`. */
export const MAPBOX_TILE_URL_PATTERN =
  /^https:\/\/api\.mapbox\.com\/v4\/[^/?#]+\/\d+\/\d+\/\d+(@2x)?\.[A-Za-z0-9.]+(\?.*)?$/i

/**
 * Workbox plugin: the cache key for a tile is its URL without the query string,
 * except the parameters that change the bytes (language / worldview / jobid —
 * mapbox-gl's own `PERSISTENT_PARAMS`). `sku` changes on every map load and
 * `access_token` on a key rotation; neither changes the tile.
 *
 * ⚠️ SELF-CONTAINED: workbox-build writes this function into sw.js with
 * Function#toString, so its body must not refer to ANYTHING outside itself —
 * no imports, no module constants, no helper functions. The page calls it
 * through `mapboxTileCacheKey` so both sides use the one implementation.
 */
export const mapboxTileCacheKeyPlugin = {
  cacheKeyWillBeUsed: async ({ request }: { request: { url: string } }): Promise<string> => {
    const url = request.url
    const q = url.indexOf('?')
    if (q < 0) return url
    const params = new URLSearchParams(url.slice(q + 1))
    const kept = new URLSearchParams()
    for (const name of ['language', 'worldview', 'jobid']) {
      const value = params.get(name)
      if (value) kept.set(name, value)
    }
    const rest = kept.toString()
    return rest ? `${url.slice(0, q)}?${rest}` : url.slice(0, q)
  },
}

/** The key the service worker stores `url` under (see the plugin above). */
export function mapboxTileCacheKey(url: string): Promise<string> {
  return mapboxTileCacheKeyPlugin.cacheKeyWillBeUsed({ request: { url } })
}

/**
 * Workbox plugin: a Mapbox entry is served for 30 days from the day it was
 * DOWNLOADED, then deleted — Mapbox Product Terms cl 2.8.1.
 *
 * Why ExpirationPlugin's `maxAgeSeconds` is not enough (workbox-expiration
 * 7.4.1): its `cachedResponseWillBeUsed` re-stamps the entry on EVERY read, so
 * its age counts from last use; and its one download-age check reads the
 * response's `Date` header, which a CORS response does not expose — no header
 * reads as fresh. A tile read regularly offline was kept, and served, for ever.
 *
 * - `cacheWillUpdate` stores a copy of each 200 response carrying the download
 *   time in `x-sw-downloaded-at` (epoch ms). Body, status and every header the
 *   page could read are kept. Anything else passes through untouched for the
 *   route's CacheableResponsePlugin to judge.
 * - `cachedResponseWillBeUsed` serves a stamped entry younger than 30 days.
 *   Older, unstamped (stored before this plugin existed) or unparseable: it
 *   deletes the entry and returns null, so CacheFirst downloads it afresh when
 *   there is signal and fails when there is not.
 *
 * ⚠️ ORDER: list it AHEAD of ExpirationPlugin. Workbox chains
 * `cachedResponseWillBeUsed`, and ExpirationPlugin returns before re-stamping
 * when it is handed null — so an expired read leaves no fresh timestamp behind.
 *
 * ⚠️ SELF-CONTAINED, like the key plugin: workbox-build copies these functions
 * into sw.js with Function#toString, so the 30 days and the header name are
 * written out inside each body, not referenced.
 *
 * ONE RULE, three callers: `cachedResponseWillBeUsed` is also what the page
 * calls to judge an entry — the daily storage sweep (mapboxCacheSweep.ts) and
 * "Save offline"'s re-download (offlineMapTiles.ts) — the same way the page
 * calls the key plugin through `mapboxTileCacheKey`. Never restate the 30-day
 * test elsewhere: a second copy is a second rule.
 */
export const mapboxCacheAgePlugin = {
  cacheWillUpdate: async ({ response }: { response: Response }): Promise<Response> => {
    // A copy can only be made of a real 200. An opaque response (status 0) can't
    // be constructed and would be refused at read time anyway.
    if (!response || response.status !== 200) return response
    const headers = new Headers(response.headers)
    headers.set('x-sw-downloaded-at', String(Date.now()))
    return new Response(await response.arrayBuffer(), {
      status: response.status,
      statusText: response.statusText,
      headers,
    })
  },
  cachedResponseWillBeUsed: async ({ cacheName, request, cachedResponse }: {
    cacheName: string
    request: Request
    cachedResponse?: Response | null
  }): Promise<Response | null> => {
    if (!cachedResponse) return null
    const downloadedAt = Number(cachedResponse.headers.get('x-sw-downloaded-at'))
    const ageMs = Date.now() - downloadedAt
    if (downloadedAt > 0 && ageMs >= 0 && ageMs <= 30 * 24 * 60 * 60 * 1000) return cachedResponse
    try {
      // `request` is the effective cache key (after any cacheKeyWillBeUsed).
      await (await caches.open(cacheName)).delete(request)
    } catch {
      // Still refuse to serve it; the next read tries the delete again.
    }
    return null
  },
}
